import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Styled } from './gateway-card.styled';
import { GatewayCardProvider } from './gateway-card.context';
import Statics from './components/statics';
import Header from './components/header';
import BlurOverlay from './components/blur-overlay';
import Information from './components/information';
import MenuDialogs from './components/menu-dialogs';
import { Gateway } from 'features/gateways/types/gateway.types';
import { timeConverter } from 'utils/utils';
import { Box } from '@mui/material';
import GatewayChart from '../overall-finance/chart/gateway-chart';
import moment from 'moment-jalaali';
import PermissionBlur from 'features/gateways/components/permission-blur';
import useGatewayAccess from 'features/gateways/hooks/use-gateway-access';
import { getGatewayLandingPath } from '../gateway-selector/gateway-switch-path';
import { useUserProfileQuery } from 'app/store';

const last7DaysObj: Record<string, number> = {};

for (let i = 6; i >= 0; i--) {
    const dateKey = moment()
        .subtract(i, 'days')
        .locale('en')
        .format('YYYY-MM-DD');
    last7DaysObj[dateKey] = 0;
}

const GatewayCard: React.FC<{
    gateway: Gateway;
    loading?: boolean;
    noMenu?: boolean;
}> = (props) => {
    const navigate = useNavigate();
    const { data: profile } = useUserProfileQuery();
    const {
        canViewTransactions,
        canEnterGateway,
    } = useGatewayAccess(props.gateway);

    const [chartWeekData, setChartWeekData] = useState<
        Record<string, number | string> | undefined
    >(undefined);

    useEffect(() => {
        setChartWeekData(props?.gateway?.statics?.charts?.week);
    }, [props?.gateway]);

    const handleOnClick = (e: any) => {
        if (props.gateway.status !== 'activated' || !canEnterGateway) {
            return;
        }

        let classNames = '';
        if (e.target.className?.baseVal !== undefined) {
            classNames = e.target.className.baseVal;
        } else {
            classNames = e.target.className;
        }
        if (
            classNames.indexOf('MuiSvgIcon-root') === -1 &&
            classNames.indexOf('MuiBackdrop-root') === -1 &&
            classNames.indexOf('MuiList-root') === -1 &&
            classNames.indexOf('MuiListItemText-primary') === -1
        ) {
            // The route drives the active gateway. DashboardLayout will sync
            // Redux after navigation, while the gateway transition masks the swap.
            navigate(getGatewayLandingPath(props.gateway, profile?.id));
        }
    };

    const isLocked = props.gateway.status !== 'activated';
    const isNavigationLocked = isLocked || !canEnterGateway;
    const isTransactionLocked = !isLocked && !canViewTransactions;

    return (
        <GatewayCardProvider value={props}>
            <>
                <Styled.Card
                    onClick={handleOnClick}
                    className={
                        isNavigationLocked ? 'gateway-card--locked' : undefined
                    }>
                    <Header />

                    <Styled.CardContent>
                        <Information
                            onClick={canEnterGateway ? handleOnClick : undefined}
                        />

                        <Box
                            sx={{
                                position: 'relative',
                                mt: 'auto',
                                pt: 1.5,
                                borderTop: (theme) =>
                                    `1px solid ${theme.palette.divider}`,
                            }}>
                            <PermissionBlur
                                allowed={!isTransactionLocked}
                                label="دسترسی تراکنش‌ها برای شما فعال نیست"
                                borderRadius={2}>
                                <Box
                                    sx={{
                                        pointerEvents:
                                            isLocked || isTransactionLocked
                                                ? 'none'
                                                : 'auto',
                                        userSelect:
                                            isLocked || isTransactionLocked
                                                ? 'none'
                                                : 'auto',
                                    }}>
                                    <GatewayChart
                                        staticChart={
                                            props?.noMenu
                                                ? {}
                                                : (chartWeekData ?? last7DaysObj)
                                        }
                                        fixedTimeline="weekly"
                                        height={140}
                                    />
                                    <Statics />
                                </Box>
                            </PermissionBlur>
                            <BlurOverlay />
                        </Box>
                    </Styled.CardContent>
                </Styled.Card>

                <MenuDialogs />
            </>
        </GatewayCardProvider>
    );
};

export default GatewayCard;

const getKeyFormat = (timeline: string, data: string) => {
    switch (timeline) {
        case 'weekly':
            return timeConverter(data, 'fullDay');
        case 'daily':
            return timeConverter(data);

        default:
            return timeConverter(data, 'date');
    }
};
